import { InfiniteData } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import { LocDomain } from '@/core/lib/new-architecture/domain/locs.domain';
import { ContactType, type UsersContactFollowsResult, type UsersContactsFollowsResult, type UsersContactsResult } from '@/core/types/contact';
import type { CityResult, CountryResult, PointResult, SubResult } from '@/core/types/geo';
import type { UsersPrivateProfilResult } from '@/core/types/profile';
import type { UserControlResult, UserResult } from '@/core/types/users';

export interface RelatedUser {
  uuid: string;
  username: string;
  state: string;
  avatarUrl: string;
  donationDonated: number;
  donationReceived: number;
  isNice: boolean;
  isPunctual: boolean;
  co2Given: number;
  co2Received: number;
  createdAt: Dayjs;
}

const userResultToRelatedUser = (user: UserResult): RelatedUser => ({
  ...user,
  avatarUrl: user.avatar_url,
  donationDonated: user.donation_donated,
  donationReceived: user.donation_received,
  isNice: user.is_nice,
  isPunctual: user.is_punctual,
  co2Given: user.co2_given,
  co2Received: user.co2_received,
  createdAt: dayjs(user.created_at),
});

const getUsers = (type: ContactType, contacts?: UsersContactsResult | null): RelatedUser[] => {
  return (contacts?.contacts ?? []).reduce((acc, contact) => {
    if (contact.type === type) {
      const user = (contacts?.users ?? []).find(u => u.uuid === contact.for_user);

      if (!user) {
        return acc;
      }

      return [...acc, userResultToRelatedUser(user)];
    }

    return acc;
  }, [] as RelatedUser[]);
};

export type FollowUser = RelatedUser & { donations: number };
const getFollowUsers = (contactsFollowsPages?: InfiniteData<UsersContactsFollowsResult>): FollowUser[] => {
  const concatContactsFollows = contactsFollowsPages?.pages
    .flatMap(page => page)
    .reduce(
      (acc, page) => {
        const properties = ['users', 'follows'] as const;
        return properties.reduce((acc, property) => ({ ...acc, [property]: [...acc[property], ...(page[property] ?? [])] }), acc);
      },
      {
        users: [] as UserResult[],
        follows: [] as UsersContactFollowsResult[],
      },
    );

  return (concatContactsFollows?.follows ?? []).reduce((acc, follow) => {
    const user = (concatContactsFollows?.users ?? []).find(u => u.uuid === follow.user);

    if (!user) {
      return acc;
    }

    return [...acc, { ...userResultToRelatedUser(user), donations: follow.donations }];
  }, [] as FollowUser[]);
};
interface CurrentDomainConstructor {
  data: UsersPrivateProfilResult;
  control?: UserControlResult | null;
  contacts?: UsersContactsResult | null;
  contactsFollowing?: InfiniteData<UsersContactsFollowsResult>;
  contactsFollowed?: InfiniteData<UsersContactsFollowsResult>;
  loc?: LocDomain | null;
}

interface NotificationParams {
  newsletters: {
    email: boolean;
    push: boolean;
  };
  conversations: {
    email: boolean;
    push: boolean;
  };
  accountLife: {
    email: boolean;
    push: boolean;
  };
}

interface CurrentInfo {
  avatarUrl: string;
  username: string;
  co2: number;
  email: string;
  signature: string | null;
  isSuperUser: boolean;
}

export interface CurrentDomainInterface {
  uuid: string;

  hasBlocked: (uuid: string) => boolean;
  getNotificationParams: () => NotificationParams;
  getInfo: () => CurrentInfo;
  getCityOrSubResult: () => SubResult | CityResult | null;
}

class CurrentDomain implements CurrentDomainInterface {
  private loc: LocDomain | null;

  public data: UsersPrivateProfilResult;

  public control: UserControlResult;

  public blockedUsers: RelatedUser[];

  public followingUsers: FollowUser[];
  public followingUsersTotal?: number;
  public followedUsers: FollowUser[];

  constructor({ data, control, contacts, loc, contactsFollowing, contactsFollowed }: CurrentDomainConstructor) {
    this.data = data;
    this.control = control ?? null;
    this.loc = loc ?? null;
    this.blockedUsers = getUsers(ContactType.BLOCK, contacts);

    this.followingUsers = getFollowUsers(contactsFollowing);
    this.followingUsersTotal = contactsFollowing?.pages[0].total;

    this.followedUsers = getFollowUsers(contactsFollowed);
  }

  get uuid() {
    return this.data.uuid;
  }

  get nbDaysWithoutAdsRemaining(): number {
    if (this.displayAds()) return 0;
    return Math.ceil(dayjs(this.data.adless).diff(dayjs(), 'days', true));
  }

  public hasBlocked(uuid: string) {
    return !!this.blockedUsers.find(user => user.uuid === uuid);
  }

  public getNotificationParams(): NotificationParams {
    return {
      newsletters: {
        email: !!this.data.newsletter,
        push: !!this.data.push,
      },
      conversations: {
        email: !!this.data.msg_email,
        push: !!this.data.msg_push,
      },
      accountLife: {
        email: !!this.data.life_email,
        push: !!this.data.life_push,
      },
    };
  }

  public getInfo(): CurrentInfo {
    return {
      avatarUrl: this.data.avatar_url,
      username: this.data.username,
      co2: this.data.co2_given + this.data.co2_received,
      email: this.data.email,
      signature: this.data.signature ?? null,
      isSuperUser: this.data.activity_points >= this.data.super_user_threshold,
    };
  }

  public getCityOrSubResult(): SubResult | CityResult | null {
    const sub = this.data.subs?.find(s => s.id === this.data.sub) ?? null;
    const city = this.data.cities?.find(c => c.id === this.data.city) ?? null;
    return sub ?? city;
  }

  public getCityResult(): CityResult | null {
    const city = this.data.cities?.find(c => c.id === this.data.city) ?? null;
    return city;
  }

  public getLoc(): LocDomain | null {
    if (this.loc) {
      return this.loc;
    }

    const userCity = this.data.cities?.find(c => c.id === this.data.city) ?? null;
    const userSub = this.data.subs?.find(c => c.id === this.data.sub) ?? null;

    if (!userCity) {
      return null;
    }

    const userAdmin = this.data.admins?.find(admin => admin.id === userCity?.admin);
    const userCountry = this.data.countries?.find(country => country.id === userCity?.country) as CountryResult;

    return new LocDomain({
      data: {
        id: this.data.city.toString(),
        country: userCountry,
        admin: userAdmin,
        city: userCity,
        sub: userSub,
      },
    });
  }

  public getLatLon(): PointResult | null {
    if (this.loc) {
      return this.loc.getLatLon();
    }
    return null;
  }

  public displayAds(): boolean {
    if (!this.data.adless) return true;
    const dateToPublishAds = dayjs(this.data.adless);
    const today = dayjs();
    return dateToPublishAds.isBefore(today, 'd');
  }
}

export default CurrentDomain;
